import { CrossIcon, IdeaIcon } from '@/assets/icons'
import { DragEndEvent, DragMoveEvent, DragOverEvent, useDndMonitor, useDroppable } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { useIdeas, useTravelSegments, useUpdateTripDayInfo } from '@hooks'
import { EventCategory, GetIdeaResponse } from '@orbit/api-types'
import React, { useRef, useState } from 'react'
import toast from 'react-hot-toast'
import AddEventModal from '../AddEventModal'
import Card from '../Card'
import DropZone from '../DropZone'
import EditDayInfoModal from '../EditDayInfoModal'
import ImportIdeaModal from '../ImportIdeaModal'
import TravelSegment from '../TravelSegment'
import {
    AddEventButton,
    AddNoteRow,
    ButtonsContainer,
    ColumnContainer,
    ColumnContent,
    ColumnHeader,
    ColumnTitle,
    CustomDayName,
    DateDisplay,
    DayNoteIcon,
    DayNoteRow,
    DayNoteText,
    EditDayInfoButton,
    EmptyState,
    EmptyStateText,
    EventContent,
    EventDot,
    EventDotColumn,
    EventDotLine,
    EventDotLineAbove,
    TimelineEventRow,
} from './Column.styles'
import { ColumnProps } from './types'

const Column: React.FC<ColumnProps> = ({ day, tripId, onAddEvent, onUpdateEvent, onDeleteEvent }) => {
	const [isAddEventModalOpen, setIsAddEventModalOpen] = useState(false)
	const [isImportIdeaModalOpen, setIsImportIdeaModalOpen] = useState(false)
	const [isEditDayInfoModalOpen, setIsEditDayInfoModalOpen] = useState(false)
	const updateDayInfo = useUpdateTripDayInfo()
	const [prefilledEventData, setPrefilledEventData] = useState<{
		title?: string
		description?: string
		category?: EventCategory
		locationPrimaryText?: string
		links?: string[]
		startTime?: string
		createdFromTripIdeaId?: number
	} | undefined>(undefined)
	const columnRef = useRef<HTMLDivElement>(null)

	// Fetch ideas for this trip
	const { data: ideasData } = useIdeas(tripId)
	// Fetch travel segments for this trip
	const { data: travelSegmentsData } = useTravelSegments(tripId)
	// zones are indicators for where you can drag an event betwwen others in a day
	const [activeZone, setActiveZone] = useState<number | null>(null)
	const [isDroppingOnColumn, setIsDroppingOnColumn] = useState<boolean>(false)
	const [initialMousePos, setInitialMousePos] = useState<{ x: number; y: number } | null>(null)
	const [isDragSessionActive, setIsDragSessionActive] = useState<boolean>(false)
	const [activeDragEventId, setActiveDragEventId] = useState<string | null>(null)

	const { setNodeRef } = useDroppable({
		id: `day-${day.id}`,
	})

	useDndMonitor({
		onDragStart: event => {
			if (event?.activatorEvent) {
				const mouseEvent = event.activatorEvent as MouseEvent
				setInitialMousePos({ x: mouseEvent.clientX, y: mouseEvent.clientY })
			}
			const activeId = event.active?.id as string
			if (activeId?.startsWith('event-')) {
				const eventId = activeId.replace('event-', '').trim()
				const isInThisColumn = day.events.some(e => e.id.toString() === eventId)
				setActiveDragEventId(isInThisColumn ? eventId : null)
			}
			setIsDragSessionActive(true)
		},
		onDragOver: (event: DragOverEvent) => {
			const overId = event.over?.id as string

			if (!overId) {
				setActiveZone(null)
				setIsDroppingOnColumn(false)
				return
			}

			if (overId.startsWith('event')) {
				const eventId = overId.replace('event-', '').trim()
				setIsDroppingOnColumn(false)

				// check to see if its relevant to this column
				const eventIsInThisColumn = day.events.some(event => event.id.toString() === eventId)

				if (!eventIsInThisColumn) {
					setActiveZone(null)
				}
			}

			if (overId.startsWith('day')) {
				setActiveZone(null)

				const dayIndex = overId.replace('day-', '').trim()
				setIsDroppingOnColumn(dayIndex === day.id)
			}
		},
		onDragMove: (event: DragMoveEvent) => {
			const overId = event.over?.id as string

			if (!overId) {
				setActiveZone(null)
				return
			}

			if (!overId.startsWith('event')) {
				return
			}

			const eventId = overId.replace('event-', '').trim()
			const eventIsInThisColumn = day.events.some(event => event.id.toString() === eventId)

			if (!eventIsInThisColumn) {
				return
			}

			setIsDroppingOnColumn(false)

			const cardElement = columnRef.current?.querySelector(
				`[data-event-id="${eventId}"]`,
			) as HTMLElement

			if (!cardElement || !initialMousePos?.y) {
				return
			}

			const cardEventIndex = parseInt(cardElement.getAttribute('data-index') || '0')
			const cardRect = cardElement.getBoundingClientRect()

			const currentMouseY = initialMousePos.y + event.delta.y

			const relativeY = currentMouseY - cardRect.top
			const normalizedPosition = (relativeY / cardRect.height) * 100
			const clampedPosition = Math.max(0, Math.min(100, normalizedPosition))

			setActiveZone(cardEventIndex + (clampedPosition < 50 ? 0 : 1))
		},
		onDragEnd: (dragEvent: DragEndEvent) => {
			if (!dragEvent.active?.id || !dragEvent.over?.id) {
				resetDragStateVars()
				return
			}

			const activeId = dragEvent.active.id as string
			const overId = dragEvent.over.id as string

			if (!activeId.startsWith('event')) {
				resetDragStateVars()
				return
			}

			const activeEventId = activeId.replace('event-', '').trim()

			// dropping inbetween events
			if (overId.startsWith('event')) {
				const overEventId = overId.replace('event-', '').trim()

				const overEventIndexWithinThisDay = day.events.findIndex(
					event => event.id.toString() === overEventId,
				)

				// since this method fires for all columns, make sure the right column consumes this action
				if (overEventIndexWithinThisDay !== -1 && activeZone != null) {
					// if you're re-arranging an event within its same day
					// the logic changes a bit since the card is temporarily "removed" from the day
					// the index in which you're trying to change it to could be off
					// so we need to adjust for it
					const activeEventIndexWithinThisDay = day.events.findIndex(
						event => event.id.toString() === activeEventId,
					)

					let targetEventIndex =
						activeEventIndexWithinThisDay !== -1 &&
						activeEventIndexWithinThisDay < overEventIndexWithinThisDay
							? activeZone - 1
							: activeZone

					onUpdateEvent(activeEventId, {
						tripDayIndex: Number(day.id),
						eventIndex: targetEventIndex,
					})
				}
			} else if (overId.startsWith('day')) {
				// dropping on a completely empty day
				const overDayId = overId.replace('day-', '').trim()
				if (day.id === overDayId) {
					onUpdateEvent(activeEventId, {
						tripDayIndex: Number(day.id),
						eventIndex: 0,
					})
				}
			}

			setActiveZone(null)
			setIsDroppingOnColumn(false)
			setIsDragSessionActive(false)
			setActiveDragEventId(null)
		},
	})

	const resetDragStateVars = () => {
		setActiveZone(null)
		setIsDroppingOnColumn(false)
		setIsDragSessionActive(false)
		setActiveDragEventId(null)
	}

	const handleAddEvent = (
		title: string,
		description: string,
		category?: EventCategory,
		location?: {
			fullText: string
			primaryText: string
			secondaryText?: string
			googlePlaceId?: string
			googleTypes: string[]
		},
		links?: string[],
		startTime?: string | null,
		createdFromTripIdeaId?: number,
	) => {
		onAddEvent(day.id, title, description, category, location, links, startTime, createdFromTripIdeaId)
		// Clear prefilled data after successful submit
		setPrefilledEventData(undefined)
	}

	const handleCloseModal = () => {
		setIsAddEventModalOpen(false)
		// Clear prefilled data when closing modal
		setPrefilledEventData(undefined)
	}

	const handleImportIdea = (idea: GetIdeaResponse) => {
		// Close import modal
		setIsImportIdeaModalOpen(false)

		// Set prefilled data from idea
		setPrefilledEventData({
			title: idea.name,
			description: idea.description || '',
			category: idea.category,
			locationPrimaryText: idea.location?.primaryText || '',
			links: idea.links?.map(link => link.url) || [],
			startTime: '',
			createdFromTripIdeaId: idea.id,
		})

		// Open add event modal with prefilled data
		setIsAddEventModalOpen(true)
	}

	const handleCloseImportModal = () => {
		setIsImportIdeaModalOpen(false)
	}

	const handleSaveDayInfo = async (name: string | null, note: string | null) => {
		try {
			await updateDayInfo.mutateAsync({
				tripId,
				dayIndex: Number(day.id),
				data: { name, note },
			})
			setIsEditDayInfoModalOpen(false)
			toast.success('Day info updated')
		} catch {
			toast.error('Failed to save day info. Please try again.')
		}
	}

	const formatDate = (dateString: string) => {
		// Parse as local Y-M-D to avoid UTC timezone shifts (e.g., showing prior day)
		const normalized = dateString.includes('-') ? dateString.split('-').join('') : dateString
		const y = Number(normalized.slice(0, 4))
		const m = Number(normalized.slice(4, 6)) - 1
		const d = Number(normalized.slice(6, 8))
		const date = new Date(y, m, d)
		return date.toLocaleDateString('en-US', {
			weekday: 'short',
			month: 'short',
			day: 'numeric',
		})
	}

	// Helper function to find travel segment between two events
	const getTravelSegmentBetweenEvents = (currentEventId: number, nextEventId: number) => {
		if (!travelSegmentsData?.segments) return null

		return travelSegmentsData.segments.find(
			segment =>
				segment.fromEventId === currentEventId &&
				segment.toEventId === nextEventId &&
				segment.tripDayIndex === Number(day.id),
		)
	}

	// Events are already sorted by eventIndex in generateItinerary
	const sortedEvents = day.events

	return (
		<ColumnContainer $isDraggingOver={isDroppingOnColumn}>
			<ColumnHeader>
				<ColumnTitle>
					{day.displayName}
					<DateDisplay>{formatDate(day.date)}</DateDisplay>
					{day.name && <CustomDayName>{day.name}</CustomDayName>}
				</ColumnTitle>
				<EditDayInfoButton
					type="button"
					onClick={() => setIsEditDayInfoModalOpen(true)}
					title="Edit day info"
				>
					✎ Edit
				</EditDayInfoButton>
			</ColumnHeader>

			{day.note ? (
				<DayNoteRow type="button" onClick={() => setIsEditDayInfoModalOpen(true)}>
					<DayNoteIcon>📝</DayNoteIcon>
					<DayNoteText>{day.note}</DayNoteText>
				</DayNoteRow>
			) : (
				<AddNoteRow type="button" onClick={() => setIsEditDayInfoModalOpen(true)}>
					<DayNoteIcon>📝</DayNoteIcon>
					Add a note…
				</AddNoteRow>
			)}

			<ColumnContent ref={day.events.length > 0 ? null : setNodeRef}>
				<SortableContext
					items={sortedEvents.map(event => `event-${event.id}`)}
					strategy={verticalListSortingStrategy}
				>
					<div ref={columnRef}>
					{sortedEvents.map((event, index) => {
						const nextEvent = sortedEvents[index + 1]
						const travelSegment = nextEvent
							? getTravelSegmentBetweenEvents(event.id, nextEvent.id)
							: null
						const isFirstEvent = index === 0
						const isLastEvent = index === sortedEvents.length - 1
						const isBeingDragged = activeDragEventId === event.id.toString()

						return (
							<React.Fragment key={event.id}>
								<DropZone isExpanded={activeZone === index} />
								{isBeingDragged ? (
									<div style={{ height: 0, overflow: 'hidden' }}>
										<EventContent data-event-id={event.id} data-index={index}>
											<Card event={event} onUpdate={onUpdateEvent} onDelete={onDeleteEvent} />
										</EventContent>
									</div>
								) : (
									<TimelineEventRow>
								<EventDotColumn>
										<EventDotLineAbove $first={isFirstEvent} />
										<EventDot />
										<EventDotLine $last={isLastEvent} />
									</EventDotColumn>
										<EventContent data-event-id={event.id} data-index={index}>
											<Card event={event} onUpdate={onUpdateEvent} onDelete={onDeleteEvent} />
										</EventContent>
									</TimelineEventRow>
								)}
								{travelSegment && (
									<TravelSegment segment={travelSegment} isHidden={isDragSessionActive} />
								)}
								{isLastEvent && (
									<DropZone isExpanded={activeZone === sortedEvents.length} />
								)}
							</React.Fragment>
						)
					})}
					</div>
				</SortableContext>

				{day.events.length === 0 && !isAddEventModalOpen && (
					<EmptyState>
						<EmptyStateText>No Events Yet</EmptyStateText>
					</EmptyState>
				)}
			</ColumnContent>

			<ButtonsContainer>
				<AddEventButton onClick={() => setIsAddEventModalOpen(true)}>
					<CrossIcon width={18} height={18} fill="currentColor" />
					Add
				</AddEventButton>
				{ideasData?.ideas && ideasData.ideas.length > 0 && (
					<AddEventButton onClick={() => setIsImportIdeaModalOpen(true)}>
						<IdeaIcon width={18} height={18} fill="currentColor" />
						Import Idea
					</AddEventButton>
				)}
			</ButtonsContainer>

		<AddEventModal
			isOpen={isAddEventModalOpen}
			onClose={handleCloseModal}
			onSubmit={handleAddEvent}
			dayName={day.date}
			initialValues={prefilledEventData}
		/>

	<ImportIdeaModal
		isOpen={isImportIdeaModalOpen}
		onClose={handleCloseImportModal}
		onNext={handleImportIdea}
		ideas={ideasData?.ideas || []}
	/>

	<EditDayInfoModal
		isOpen={isEditDayInfoModalOpen}
		onClose={() => setIsEditDayInfoModalOpen(false)}
		onSave={handleSaveDayInfo}
		isSaving={updateDayInfo.isPending}
		initialName={day.name}
		initialNote={day.note}
		dayLabel={formatDate(day.date)}
	/>
	</ColumnContainer>
)
}

export default Column
